import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useMatch, useNavigate, useParams } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { isPendingFirstInstantConnectivityDocument } from 'support/helpers/relationships/relationships';
import { RelationshipStatus, RelationshipV2 } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { TabsList } from 'components/Navigation/Tabs/TabsList';

import { ConnectionSetupStep } from '../../hooks/useSetupConnectionSteps/useSetupConnectionSteps';
import { ConnectionSetupTab } from './ConnectionSetupTab/ConnectionSetupTab';
import { NotificationsManagementTab } from './NotificationsManagementTab/NotificationsManagementTab';
import { TransactionsTab } from './TransactionsTab/TransactionsTab';

type RelationshipDetailsTabsProps = {
  relationship: RelationshipV2;
  steps: Array<ConnectionSetupStep>;
};

const TRANSACTIONS_TAB = 'transactions';
const CONNECTION_SETUP_TAB = 'connectionSetup';
const NOTIFICATIONS_MANAGEMENT_TAB = 'notificationsManagement';

const TAB_ORDER = [
  { key: TRANSACTIONS_TAB, subPath: 'relationshipTransactions' },
  { key: CONNECTION_SETUP_TAB, subPath: 'relationshipSetup' },
  { key: NOTIFICATIONS_MANAGEMENT_TAB, subPath: 'relationshipNotifications' },
] as const;

export const RelationshipDetailsTabs = ({ relationship, steps }: RelationshipDetailsTabsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerId, relationshipId } = useParams<{ partnerId: string; relationshipId: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { getRoute, routeTo } = useConnectionNavigation();

  const isLiveOrActive = [RelationshipStatus.LIVE, RelationshipStatus.ACTIVE].includes(
    RelationshipStatus[relationship.status],
  );

  // `useMatch` is exact, so the base match never matches a tab or slide-over path.
  const isBaseRoute = Boolean(useMatch(getRoute('relationshipDetails')));
  const isTransactionsRoute = Boolean(useMatch(getRoute('relationshipTransactions')));
  const isNotificationsRoute = Boolean(useMatch(getRoute('relationshipNotifications')));

  const tabDefinitions = useMemo(
    () => [
      { key: TRANSACTIONS_TAB, label: t('connections:relationshipDetails.tabs.transactions') },
      { key: CONNECTION_SETUP_TAB, label: t('connections:relationshipDetails.tabs.connectionSetup') },
      { key: NOTIFICATIONS_MANAGEMENT_TAB, label: t('connections:relationshipDetails.tabs.notificationsManagement') },
    ],
    [t],
  );

  const tabs = useMemo(() => tabDefinitions.map(({ label }) => ({ label })), [tabDefinitions]);

  if (!isLiveOrActive) {
    return <ConnectionSetupTab relationship={relationship} steps={steps} />;
  }

  if (isBaseRoute && partnerId && relationshipId) {
    const defaultSubPath = isPendingFirstInstantConnectivityDocument(relationship)
      ? 'relationshipSetup'
      : 'relationshipTransactions';
    return <Navigate replace to={routeTo(defaultSubPath, { partnerId, relationshipId })} />;
  }

  const selectedIndex = getSelectedTabIndex(tabDefinitions, { isTransactionsRoute, isNotificationsRoute });
  const selectedKey = tabDefinitions[selectedIndex]?.key;

  const onTabChange = (index: number) => {
    const target = TAB_ORDER[index];
    if (!target || !partnerId || !relationshipId) {
      return;
    }
    navigate(routeTo(target.subPath, { partnerId, relationshipId }));
  };

  return (
    <div className="-mt-4 space-y-6">
      <TabsList tabs={tabs} onChange={onTabChange} selected={selectedIndex} />
      {selectedKey === TRANSACTIONS_TAB && <TransactionsTab relationship={relationship} />}
      {selectedKey === CONNECTION_SETUP_TAB && <ConnectionSetupTab relationship={relationship} steps={steps} />}
      {selectedKey === NOTIFICATIONS_MANAGEMENT_TAB && currentPartner && (
        <NotificationsManagementTab
          relationship={relationship}
          currentPartnerId={currentPartner.id}
          currentPartnerType={currentPartner.partnerType}
        />
      )}
    </div>
  );
};

// Slide-over routes (test/go-live/instant-connectivity/task) render the tab bar too; they all
// belong to the Connection Setup tab, so it is the fallback when no tab segment matches.
const getSelectedTabIndex = (
  tabDefinitions: Array<{ key: string; label: string }>,
  { isTransactionsRoute, isNotificationsRoute }: { isTransactionsRoute: boolean; isNotificationsRoute: boolean },
): number => {
  const selectedKey = isTransactionsRoute
    ? TRANSACTIONS_TAB
    : isNotificationsRoute
      ? NOTIFICATIONS_MANAGEMENT_TAB
      : CONNECTION_SETUP_TAB;

  return tabDefinitions.findIndex(({ key }) => key === selectedKey);
};
